import type { ShopBundleView, ShopCategory, ShopItemView } from '@bioforge/protocol';
import { Container, FillGradient, Graphics, Text } from 'pixi.js';
import { HAT_FRAME } from './art/characters.js';
import { BOSS_NAMES } from './labels.js';
import { listen, type AppContext, type Navigator, type Scene } from './scene.js';
import { tr } from './story/story.js';
import { DesignFrame, currentFrame, screenBackground } from './ui/frame.js';
import { ScrollView } from './ui/scroll.js';
import { microbeIdle } from './ui/motion.js';
import { artSprite } from './ui/textures.js';
import { COLOR, textStyle } from './ui/theme.js';
import { NetChip, UiButton, panel, vertical } from './ui/widgets.js';
import { JarView, drawCapsule, jarFromRows, type JarSkin } from './view/jar-view.js';
import { formatPrice, previewSkin, skinOf } from './view/skins.js';

const CATEGORIES: readonly {
  readonly id: ShopCategory;
  readonly label: string;
  readonly soon: string;
}[] = [
  { id: 'microbes', label: 'Скины микробов', soon: '' },
  { id: 'vessels', label: 'Сосуды', soon: '' },
  { id: 'capsules', label: 'Капсулы', soon: '' },
  { id: 'labs', label: 'Лаборатории', soon: 'Фоны, освещение и декор лаборатории.' },
  { id: 'effects', label: 'Эффекты', soon: 'Эффекты очистки: конфетти, пузыри, искры.' },
  { id: 'profile', label: 'Профиль', soon: 'Аватары, рамки и титулы — их видят соперники.' },
];

/** Банка предпросмотра (макет «Банка», пресет «preview»): все три вида микробов и капсулы. */
const PREVIEW_ROWS = [
  '.. .. .. .. .. .. .. ..',
  '.. .. .. L0a R1a .. .. ..',
  '.. .. .. .. .. .. .. ..',
  '.. V2 .. .. .. .. V0 ..',
  '.. .. .. .. .. V1 .. ..',
  'V1 .. U2 .. .. .. .. V2',
  '.. V0 D2 .. V1 .. V0 ..',
  'V2 .. V1 L0 R1 V2 .. V0',
  '.. V1 .. V2 V0 .. V1 ..',
];

/** Раскладка: сетка и предпросмотр рядом (макет) или одной прокручиваемой колонкой (телефон). */
interface Layout {
  readonly gridX: number;
  readonly gridW: number;
  readonly cols: number;
  /** Верх разделов и товаров. */
  readonly tabsY: number;
  readonly top: number;
  readonly asideX: number;
  readonly asideW: number;
}

const WIDE: Layout = {
  gridX: 60,
  gridW: 842,
  cols: 3,
  tabsY: 96,
  top: 160,
  asideX: 930,
  asideW: 450,
};
const TALL: Layout = { gridX: 0, gridW: 676, cols: 2, tabsY: 0, top: 0, asideX: 0, asideW: 676 };

const caps = (text: string, color: number) =>
  new Text({
    text: text.toUpperCase(),
    style: textStyle({ size: 11, display: true, weight: '800', color, letterSpacing: 1.5 }),
  });

/**
 * Магазин по макету «Магазин» (SPEC.md, раздел 18): только косметика, каталог и предметы игрока
 * приходят с сервера. Оплаты в MVP нет — кнопка покупки неактивна; награды за боссов надеваются.
 */
export class ShopScene implements Scene {
  readonly view: DesignFrame;
  private category: ShopCategory = 'microbes';
  private selected: ShopItemView | null = null;
  /** Предпросмотр «Со скином» или «Стандарт» — сравнение читаемости (18.6). */
  private withSkin = true;
  private busy = false;
  private notice = '';
  private readonly tabs = new Container();
  private readonly body = new Container();
  private readonly aside = new Container();
  private readonly stops: (() => void)[] = [];
  private readonly tall: boolean;
  private readonly lay: Layout;
  /** Телефон: разделы, товары и предпросмотр прокручиваются одной колонкой. */
  private scroll: ScrollView | null = null;
  private readonly listeners: (() => void)[] = [];

  constructor(
    private readonly nav: Navigator,
    private readonly ctx: AppContext,
  ) {
    const frame = currentFrame();
    this.tall = frame.width < frame.height;
    this.lay = this.tall ? TALL : WIDE;
    this.view = new DesignFrame(screenBackground(0.4, 0.4, [], frame), frame);
    this.header();
    if (this.tall) {
      this.tabs.position.set(22, 136);
      this.scroll = new ScrollView(676, 1280 - 200 - 12);
      this.scroll.position.set(22, 200);
      this.scroll.content.addChild(this.body, this.aside);
      this.view.content.addChild(this.tabs, this.scroll);
    } else {
      this.view.content.addChild(this.tabs, this.body, this.aside);
    }
    this.listeners.push(listen('keydown', (e) => this.onKey(e)));
    this.pick('microbes');
  }

  private get shop() {
    return this.ctx.shop;
  }

  private items(): ShopItemView[] {
    return this.shop.items.filter((item) => item.category === this.category);
  }

  private header(): void {
    const root = this.view.content;
    const back = new UiButton({
      width: 104,
      height: 44,
      radius: 12,
      label: 'Меню',
      fontSize: 15,
      center: true,
      icon: { key: 'icon-back', size: 18 },
      onTap: () => this.nav.menu(),
    });
    back.position.set(60, 22);
    const title = new Text({
      text: 'Магазин',
      style: textStyle({ size: 30, display: true, weight: '900', shadow: 3 }),
    });
    title.anchor.set(0, 0.5);
    title.position.set(60 + 104 + 18, 44);
    root.addChild(back, title);

    // Справа: оплата пока недоступна (18), индикатор соединения всегда виден (18.6).
    const info = new Container();
    const text = new Text({
      text: 'Покупки скоро — награды за боссов уже можно надеть',
      style: textStyle({ size: 14, color: COLOR.goldSoft }),
    });
    text.anchor.set(0, 0.5);
    text.position.set(14, 22);
    info.addChild(
      new Graphics()
        .roundRect(0, 0, text.width + 28, 44, 12)
        .fill({ color: COLOR.gold, alpha: 0.1 })
        .stroke({ width: 2, color: COLOR.gold, alpha: 0.45 }),
      text,
    );
    const chip = new NetChip();
    root.addChild(info, chip);
    this.listeners.push(
      this.ctx.connection.watch((state) => {
        chip.set(state);
        if (this.tall) {
          // Телефон: строка под заголовком, ужатая по ширине.
          chip.position.set(720 - 22 - chip.width, 80);
          const room = chip.x - 12 - 22;
          info.scale.set(Math.min(1, room / (info.width / info.scale.x)));
          info.position.set(22, 80);
        } else {
          chip.position.set(1440 - 60 - chip.width, 22);
          info.position.set(chip.x - 12 - info.width, 22);
        }
      }),
    );
  }

  private pick(category: ShopCategory): void {
    this.category = category;
    this.selected = this.items()[0] ?? null;
    this.withSkin = true;
    this.render();
  }

  private render(): void {
    this.stops.splice(0).forEach((stop) => stop());
    for (const box of [this.tabs, this.body, this.aside]) {
      box.removeChildren().forEach((child) => child.destroy({ children: true }));
    }
    this.renderTabs();
    this.renderBody();
    this.renderAside();
    this.scroll?.refresh();
  }

  private renderTabs(): void {
    let x = this.lay.gridX;
    for (const { id, label } of CATEGORIES) {
      const active = id === this.category;
      const t = new Text({
        text: label,
        style: textStyle({ size: 14, weight: '700', color: active ? COLOR.ink : COLOR.textSoft }),
      });
      const w = t.width + 32;
      const tab = new Container();
      const bg = new Graphics();
      if (active) {
        bg.roundRect(0, 3, w, 44, 12)
          .fill(COLOR.ink)
          .roundRect(0, 0, w, 44, 12)
          .fill(COLOR.mint)
          .stroke({ width: 2, color: COLOR.ink });
      } else {
        bg.roundRect(0, 0, w, 44, 12)
          .fill({ color: COLOR.panelBottom, alpha: 0.75 })
          .stroke({ width: 2, color: COLOR.borderDim });
      }
      t.anchor.set(0.5);
      t.position.set(w / 2, 22);
      tab.addChild(bg, t);
      tab.position.set(x, this.lay.tabsY);
      tab.eventMode = 'static';
      tab.cursor = 'pointer';
      tab.accessible = true;
      tab.accessibleType = 'button';
      tab.accessibleTitle = active ? `${label} (открыт)` : label;
      tab.on('pointertap', () => this.pick(id));
      this.tabs.addChild(tab);
      x += w + 8;
    }
    // Шесть разделов шире телефона — ужимаем ряд целиком.
    if (this.tall) {
      this.tabs.scale.set(1);
      this.tabs.scale.set(Math.min(1, 676 / this.tabs.width));
    }
  }

  private renderBody(): void {
    let y = this.lay.top;
    const bundle = this.shop.bundles[0];
    if (bundle && this.category === 'microbes') {
      this.body.addChild(this.banner(bundle, y));
      y += 150 + 20;
    }
    const items = this.items();
    if (items.length === 0) {
      const soon = CATEGORIES.find((c) => c.id === this.category)?.soon ?? '';
      const box = panel({ width: this.lay.gridW, height: 160, radius: 22, alpha: 0.85 });
      box.position.set(this.lay.gridX, y);
      const title = new Text({
        text: 'Скоро в магазине',
        style: textStyle({ size: 22, display: true, weight: '900', shadow: 3 }),
      });
      title.position.set(28, 36);
      const text = new Text({ text: soon, style: textStyle({ size: 16, color: COLOR.textSoft }) });
      text.position.set(28, 84);
      box.addChild(title, text);
      this.body.addChild(box);
      return;
    }
    const gap = 16;
    const cols = this.lay.cols;
    const w = (this.lay.gridW - (cols - 1) * gap) / cols;
    items.forEach((item, i) => {
      const card = this.card(item, w);
      card.position.set(
        this.lay.gridX + (i % cols) * (w + gap),
        y + Math.floor(i / cols) * (214 + gap),
      );
      this.body.addChild(card);
    });
  }

  /** Набор недели: превью предметов, цена со скидкой; кнопка покупки пока неактивна. */
  private banner(bundle: ShopBundleView, y: number): Container {
    const box = new Container();
    box.position.set(this.lay.gridX, y);
    box.addChild(
      new Graphics()
        .roundRect(-3, -3, this.lay.gridW + 6, 156, 25)
        .fill(COLOR.ink)
        .roundRect(0, 0, this.lay.gridW, 150, 22)
        .fill(
          new FillGradient({
            type: 'linear',
            start: { x: 0, y: 0.3 },
            end: { x: 1, y: 0.7 },
            colorStops: [
              { offset: 0, color: 0x1b4a6e },
              { offset: 0.55, color: 0x2a2560 },
              { offset: 1, color: 0x4a1e46 },
            ],
            textureSpace: 'local',
          }),
        ),
    );
    const glow = new Graphics()
      .roundRect(-3, -3, this.lay.gridW + 6, 156, 25)
      .stroke({ width: 3, color: COLOR.gold, alpha: 0.35 });
    box.addChild(glow);
    let x = 26;
    // На телефоне микробы набора мельче — остаётся место под текст.
    const sizes = this.tall ? [62, 50, 56] : [86, 70, 78];
    bundle.items
      .map((id) => this.shop.items.find((item) => item.id === id))
      .filter((item): item is ShopItemView => item?.category === 'microbes')
      .slice(0, 3)
      .forEach((item, i) => {
        const size = sizes[i] ?? 70;
        const art = this.microbeArt(item, size, i * 0.6);
        art.position.set(x + size / 2, 75 + (86 - size) / 4);
        box.addChild(art);
        x += size + 2;
      });
    const tx = x + 22;
    const kicker = caps(`Набор · ${tr(bundle.title)}`, COLOR.gold);
    kicker.position.set(tx, 34);
    const desc = new Text({
      text: tr(bundle.description),
      style: textStyle({
        size: 15,
        color: 0xd6ecf5,
        wrap: this.lay.gridW - tx - 230,
        lineHeight: 21,
      }),
    });
    desc.position.set(tx, 58);
    const full = new Text({
      text: formatPrice(bundle.fullPrice.amount),
      style: textStyle({ size: 15, color: COLOR.textSoft }),
    });
    full.anchor.set(1, 0);
    full.position.set(this.lay.gridW - 26, 30);
    box.addChild(
      kicker,
      desc,
      full,
      new Graphics()
        .moveTo(full.x - full.width, 40)
        .lineTo(full.x, 40)
        .stroke({ width: 1.5, color: COLOR.textSoft }),
    );
    const buy = new UiButton({
      width: 200,
      height: 54,
      label: `Скоро · ${formatPrice(bundle.price.amount)}`,
      variant: 'gold',
      center: true,
      fontSize: 16,
      disabled: !this.shop.purchasesEnabled,
      onTap: () => undefined,
    });
    buy.accessibleTitle = `Набор «${tr(bundle.title)}» за ${formatPrice(bundle.price.amount)}: покупки скоро`;
    buy.position.set(this.lay.gridW - 26 - 200, 62);
    box.addChild(buy);
    return box;
  }

  /** Микроб со скином предмета (колпак или очки). */
  private microbeArt(item: ShopItemView, size: number, delay = 0): Container {
    const color = Number(item.slot.replace('microbe-', ''));
    // Анимация качает внутренний контейнер; внешний ставит на место вызывающий код.
    const outer = new Container();
    const art = new Container();
    art.addChild(artSprite(`microbe-${color}`, size));
    art.addChild(
      artSprite(item.look === 'santa' ? 'hat-santa' : `hat-shades-${color}`, size * HAT_FRAME),
    );
    outer.addChild(art);
    this.stops.push(microbeIdle(art, color, delay));
    return outer;
  }

  /** Рисунок предмета на карточке: микроб, маленькая банка или пара капсул. */
  private itemArt(item: ShopItemView): Container {
    if (item.category === 'microbes') return this.microbeArt(item, 92);
    if (item.category === 'capsules') {
      const g = new Graphics();
      const s = 40;
      drawCapsule(g, 1, 'L', -s, -s - 4, s, false, false, true, true);
      drawCapsule(g, 0, 'R', 0, -s - 4, s, false, false, true, true);
      drawCapsule(g, 2, 'L', -s, 4, s, false, false, true, true);
      drawCapsule(g, 1, 'R', 0, 4, s, false, false, true, true);
      return g;
    }
    const jar = new JarView(10);
    jar.render({
      width: 8,
      height: 5,
      cells: jarFromRows(PREVIEW_ROWS.slice(4)),
      skin: { vessel: item.look === 'aquarium' || item.look === 'snowglobe' ? item.look : null },
    });
    jar.pivot.set(56, 58);
    this.stops.push(() => jar.destroy());
    return jar;
  }

  private status(item: ShopItemView): { label: string; tone: 'owned' | 'reward' | 'price' } {
    if (this.shop.equipped[item.slot] === item.id) return { label: 'Надето', tone: 'owned' };
    if (this.shop.owned.includes(item.id)) return { label: 'Ваш', tone: 'owned' };
    if (item.reward)
      return { label: `Награда: победить ${BOSS_NAMES[item.reward.bossId]}`, tone: 'reward' };
    return { label: item.price ? formatPrice(item.price.amount) : '—', tone: 'price' };
  }

  private card(item: ShopItemView, w: number): Container {
    const selected = item.id === this.selected?.id;
    const box = new Container();
    const h = 214;
    const bg = new Graphics();
    if (selected) {
      bg.roundRect(-3, -3, w + 6, h + 6, 21).fill(COLOR.ink);
      bg.roundRect(0, 0, w, h, 18)
        .fill(vertical(0x245678, 0x0e1c3a))
        .stroke({ width: 3, color: COLOR.mint });
    } else {
      bg.roundRect(0, 0, w, h, 18)
        .fill({ color: COLOR.panelBottom, alpha: 0.78 })
        .stroke({ width: 2, color: COLOR.borderDim });
    }
    bg.roundRect(14, 12, w - 28, 112, 14).fill({ color: COLOR.mint, alpha: 0.06 });
    box.addChild(bg);
    const art = this.itemArt(item);
    art.position.set(w / 2, 12 + 56);
    box.addChild(art);
    const title = new Text({
      text: tr(item.title),
      style: textStyle({ size: 14, display: true, weight: '800', wrap: w - 28, lineHeight: 18 }),
    });
    title.position.set(14, 132);
    const { label, tone } = this.status(item);
    const chipText = new Text({
      text: label,
      style: textStyle({
        size: tone === 'reward' ? 12 : 12,
        display: tone !== 'reward',
        weight: tone === 'reward' ? '600' : '800',
        color: tone === 'owned' ? COLOR.mint : tone === 'reward' ? 0xffb3c8 : COLOR.ink,
      }),
    });
    const chip = new Graphics()
      .roundRect(14, h - 14 - 24, chipText.width + 20, 24, 8)
      .fill(
        tone === 'owned'
          ? { color: COLOR.teal, alpha: 0.18 }
          : tone === 'reward'
            ? { color: COLOR.pinkDark, alpha: 0.2 }
            : { color: COLOR.gold, alpha: 1 },
      );
    chipText.position.set(24, h - 14 - 24 + 4);
    box.addChild(title, chip, chipText);
    box.eventMode = 'static';
    box.cursor = 'pointer';
    box.accessible = true;
    box.accessibleType = 'button';
    box.accessibleTitle = `${tr(item.title)}. ${label}`;
    box.on('pointertap', () => {
      this.selected = item;
      this.withSkin = true;
      this.render();
    });
    return box;
  }

  private renderAside(): void {
    const height = 864 - 96;
    // На телефоне предпросмотр — под товарами.
    if (this.tall) this.aside.position.set(0, this.body.height + 20);
    else this.aside.position.set(this.lay.asideX, 96);
    this.aside.addChild(panel({ width: this.lay.asideW, height, radius: 24, alpha: 0.94 }));
    const kicker = caps('Предпросмотр', COLOR.mint);
    kicker.position.set(22, 30);
    this.aside.addChild(kicker);
    const item = this.selected;

    // «Со скином» / «Стандарт».
    const toggle = new Container();
    const options: [string, boolean][] = [
      ['Со скином', true],
      ['Стандарт', false],
    ];
    let tx = 4;
    const parts = options.map(([label, on]) => {
      const active = on === this.withSkin;
      const t = new Text({
        text: label,
        style: textStyle({ size: 13, weight: '600', color: active ? COLOR.ink : COLOR.textSoft }),
      });
      const w = t.width + 24;
      const b = new Container();
      const g = new Graphics().roundRect(0, 0, w, 34, 9);
      if (active) g.fill(COLOR.mint);
      else g.fill({ color: COLOR.ink, alpha: 0.001 });
      t.anchor.set(0.5);
      t.position.set(w / 2, 17);
      b.addChild(g, t);
      b.x = tx;
      b.y = 4;
      tx += w + 4;
      b.eventMode = 'static';
      b.cursor = 'pointer';
      b.accessible = true;
      b.accessibleType = 'button';
      b.accessibleTitle = active ? `${label} (выбрано)` : label;
      b.on('pointertap', () => {
        this.withSkin = on;
        this.render();
      });
      return b;
    });
    toggle.addChild(
      new Graphics()
        .roundRect(0, 0, tx, 42, 12)
        .fill({ color: COLOR.night, alpha: 0.6 })
        .stroke({ width: 2, color: COLOR.borderDim }),
      ...parts,
    );
    toggle.position.set(this.lay.asideW - 22 - tx, 16);
    this.aside.addChild(toggle);

    // Банка с надетыми скинами; выбранный предмет — вместо надетого в его слоте.
    const skin: JarSkin =
      this.withSkin && item ? previewSkin(this.shop, item) : this.withSkin ? skinOf(this.shop) : {};
    const jar = new JarView(30);
    jar.render({ width: 8, height: PREVIEW_ROWS.length, cells: jarFromRows(PREVIEW_ROWS), skin });
    jar.position.set((this.lay.asideW - (8 * 30 + 32)) / 2, 74);
    this.aside.addChild(jar);
    this.stops.push(() => jar.destroy());

    if (!item) return;
    let y = 74 + PREVIEW_ROWS.length * 30 + 66 + 20;
    const title = new Text({
      text: tr(item.title),
      style: textStyle({
        size: 22,
        display: true,
        weight: '900',
        wrap: this.lay.asideW - 44,
        lineHeight: 27,
      }),
    });
    title.position.set(22, y);
    y += title.height + 8;
    const desc = new Text({
      text: tr(item.description),
      style: textStyle({ size: 14, color: 0xd6ecf5, wrap: this.lay.asideW - 44, lineHeight: 21 }),
    });
    desc.position.set(22, y);
    y += desc.height + 12;
    this.aside.addChild(title, desc);
    const promise =
      item.category === 'capsules'
        ? 'Цвета половин и значки остаются различимыми'
        : item.category === 'vessels'
          ? 'Сетка и клетки видны как обычно'
          : 'Цвет и форма микроба сохраняются';
    for (const line of ['Только внешний вид — без игровых преимуществ', promise]) {
      const check = new Graphics()
        .moveTo(22, y + 8)
        .lineTo(26, y + 12)
        .lineTo(34, y + 3)
        .stroke({ width: 3, color: COLOR.teal, cap: 'round', join: 'round' });
      const t = new Text({ text: line, style: textStyle({ size: 13, color: COLOR.textSoft }) });
      t.position.set(42, y - 1);
      this.aside.addChild(check, t);
      y += 22;
    }
    if (this.notice) {
      const notice = new Text({
        text: this.notice,
        style: textStyle({ size: 13, color: 0xffb3c8, wrap: this.lay.asideW - 44 }),
      });
      notice.position.set(22, height - 22 - 58 - 10 - notice.height);
      this.aside.addChild(notice);
    }
    this.aside.addChild(this.actionButton(item, height));
  }

  /** Надеть / снять своё; награда — только за победу; продаваемое — «Скоро» (оплаты нет). */
  private actionButton(item: ShopItemView, height: number): UiButton {
    const w = this.lay.asideW - 44;
    const owned = this.shop.owned.includes(item.id);
    const worn = this.shop.equipped[item.slot] === item.id;
    const button = owned
      ? new UiButton({
          width: w,
          height: 58,
          label: worn ? 'Снять' : 'Надеть',
          variant: worn ? 'secondary' : 'primary',
          center: true,
          fontSize: 18,
          disabled: this.busy,
          onTap: () => this.equip(item, !worn),
        })
      : item.reward
        ? new UiButton({
            width: w,
            height: 58,
            label: `Победите: ${BOSS_NAMES[item.reward.bossId]}`,
            variant: 'pink',
            center: true,
            fontSize: 16,
            disabled: true,
            onTap: () => undefined,
          })
        : new UiButton({
            width: w,
            height: 58,
            label: `Скоро · ${item.price ? formatPrice(item.price.amount) : ''}`,
            variant: 'gold',
            center: true,
            fontSize: 18,
            disabled: !this.shop.purchasesEnabled,
            onTap: () => undefined,
          });
    button.position.set(22, height - 22 - 58);
    return button;
  }

  private equip(item: ShopItemView, on: boolean): void {
    if (this.busy) return;
    this.busy = true;
    this.notice = '';
    this.ctx.api.equip({ slot: item.slot, itemId: on ? item.id : null }).then(
      (shop) => {
        this.ctx.shop = shop;
        this.busy = false;
        if (!this.view.destroyed) this.render();
      },
      () => {
        this.busy = false;
        this.notice = 'Не удалось сохранить. Проверьте соединение и попробуйте ещё раз.';
        if (!this.view.destroyed) this.render();
      },
    );
  }

  /** Esc — в меню; стрелки — по карточкам; Enter — надеть или снять своё. */
  private onKey(e: KeyboardEvent): void {
    if (e.code === 'Escape') {
      this.nav.menu();
      return;
    }
    const items = this.items();
    const step = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -3, ArrowDown: 3 }[e.code];
    if (step !== undefined && items.length > 0) {
      e.preventDefault();
      const index = items.findIndex((i) => i.id === this.selected?.id);
      this.selected = items[Math.max(0, Math.min(items.length - 1, index + step))] ?? null;
      this.withSkin = true;
      this.render();
      return;
    }
    const item = this.selected;
    if (e.code === 'Enter' && item && this.shop.owned.includes(item.id)) {
      this.equip(item, this.shop.equipped[item.slot] !== item.id);
    }
  }

  destroy(): void {
    this.listeners.splice(0).forEach((off) => off());
    this.stops.splice(0).forEach((stop) => stop());
    this.view.destroy();
  }
}
